// @flow
import 'whatwg-fetch';
import type { Config, AjaxHttpResponse } from 'app/packages/api/types';

export const keepaliveIsSupported = () => 'keepalive' in new Request(''); // https://github.com/github/fetch/issues/700#issuecomment-484523384

export default function fetchRequest({
  data,
  headers,
  keepalive,
  timeout,
  type: method,
  updateProgress,
  url,
}: Config): {| promise: Promise<AjaxHttpResponse>, abort: () => void |} {
  let aborted: false | 'timeout' | 'manual' = false;

  const promise = new Promise((resolve, reject) => {
    // Build timeout callback
    const abortTimeout = timeout
      ? setTimeout(() => {
          aborted = 'timeout';
          reject({ type: 'timeout' });
        }, timeout)
      : undefined;

    fetch(url, {
      method,
      mode: 'same-origin',
      keepalive,
      headers,
      ...(data ? { body: JSON.stringify({ data }) } : {}),
    })
      .then(({ status, statusText, json, text, headers }) => {
        if (abortTimeout) {
          clearTimeout(abortTimeout);
        }
        if (aborted) {
          if (aborted !== 'timeout') {
            reject({ type: 'abort' });
          }
          return;
        }
        resolve({
          status,
          statusText,
          ok: (status >= 200 && status < 300) || status === 304,
          headers: {
            get: headers.get,
            _logString: () => {
              // we want to return what XMLHttpRequest.getAllResponseHeaders() returns
              // see https://developer.mozilla.org/en-US/docs/Web/API/XMLHttpRequest/getAllResponseHeaders
              const headerLines = [];
              for (const [name, value] of headers.entries()) {
                headerLines.push(`${name}: ${value}\r\n`);
              }
              return headerLines.join('');
            },
          },
          json: () =>
            json().then((json) => {
              if (!(json instanceof Object)) {
                return Promise.reject(new Error('JSON.parse did not return an object'));
              }
              return json;
            }),
          text,
        });
      })
      .catch(() => Promise.reject({ type: 'timeout' })); // TODO: better type here
  });

  return {
    promise,
    abort: () => {
      aborted = 'manual';
    },
  };
}
